import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { Heading } from 'components/Heading';
import { RichTextContent } from 'components/RichTextContent';
import { ScoreBadge } from 'components/ScoreBadge';
import { navigateBack, requireGameLoaded, useOnline } from 'engine/api';
import { useReadonly, useStore, useTask } from 'engine/store';
import { CheckCheck, FileQuestion, Info, TriangleAlert } from 'lucide-react';
import { useEffect, type FC } from 'react';
import type { GameTask, MultipleChoiceAnswer } from 'shared/modelTypes/game';
import { OpenQuestionForm } from 'components/forms/OpenQuestionForm';
import { MobileScreen } from 'components/MobileScreen';
import type {
	TaskAnswer,
	TaskAnswerMultipleChoice,
	TaskAnswerPhoto,
	TaskAnswerSingleChoice,
	TaskAnswerText,
	TaskAnswerVideo,
	TaskState,
	TaskStateLocal,
} from 'shared/modelTypes/client';
import { setExtra as sentrySetExtra } from '@sentry/browser';
import { Para } from 'components/Para';
import { PhotoQuestionForm } from 'components/forms/PhotoQuestionForm';
import { MultipleChoiceQuestionForm, MultiSelectMultipleChoiceQuestionForm } from 'components/forms/MultipleChoiceQuestionForm';
import { BunnyMediaRenderer } from 'components/BunnyMediaRenderer';
import { EmptyStateComponent } from 'components/EmptyStateComponent';
import { ARFormComponent } from 'components/ARFormComponent';
import { VideoQuestionForm } from 'components/forms/VideoQuestionForm';
import { BingoFormComponent } from 'components/BingoFormComponent';

export const Route = createFileRoute('/rounds/$roundId/task/$taskId')({
	component: RouteComponent,
	beforeLoad: requireGameLoaded,
});

function RouteComponent() {
	const params = Route.useParams();
	const navigate = useNavigate();

	const game = useStore((s) => s.game);
	const round = game?.rounds.find((r) => r.id == params.roundId);
	const { state: taskState, task } = useTask(params.roundId, params.taskId);

	return (
		<>
			{!task && (
				<EmptyStateComponent
					icon={FileQuestion}
					title="Task not found"
					action={{
						label: 'Return to round',
						onClick: () => navigate({ to: '/rounds/$roundId', params: { roundId: params.roundId } }),
					}}
				/>
			)}
			{task && <TaskScreen roundId={params.roundId} roundTitle={round!.title} task={task} taskState={taskState} />}
		</>
	);
}

export const TaskScreen: FC<{ roundId: string; roundTitle?: string; task: GameTask; taskState: TaskStateLocal }> = ({
	roundId,
	roundTitle,
	task,
	taskState,
}) => {
	const isReadonly = useReadonly();
	const isOnline = useOnline();

	const game = useStore((s) => s.game);
	const isFrozen = useStore((s) => s.isFrozen);
	const eventId = useStore((s) => s.eventId);
	const navigate = useNavigate();

	const submitTaskSubmission = useStore((s) => s.submitTaskSubmission);

	const router = useRouter();

	const onSubmit = (answer: TaskAnswer | File, message?: string) => {
		sentrySetExtra('lastSubmit', {
			isOnline,
			roundId,
			eventId,
			answer,
			task,
			game,
		});
		const submission: TaskState = {
			taskId: task.id,
			answer:
				answer instanceof File
					? ({
							kind: answer.type.startsWith('video/') ? 'video' : 'photo',
							id: '',
							filename: '',
							mimeType: answer.type,
							message,
						} as TaskAnswerPhoto | TaskAnswerVideo)
					: answer,

			// unused fields
			status: 'in-progress',
			unlocked: true,
			pointsGiven: 0,
			attempts: 0,
		};
		// TODO: make submitTaskSubmission take some smaller object instead of the whole taskState
		submitTaskSubmission(submission, answer instanceof File ? answer : undefined);

		// when automaticNavigation is per-task, we want to stay on the /current-task page
		// as it will automatically update with the new current task state

		const attemptsLeft = (task.attempts ?? 0) - (taskState.attempts ?? 0) - 1;
		const canRetryIfWrong = task.attempts === 0 ? true : attemptsLeft > 0;
		const isAutomaticValidationWithRetry = task.type === 'automatic-validation' && canRetryIfWrong;

		if (game?.automaticNavigation !== 'per-task' && !isAutomaticValidationWithRetry) {
			navigateBack(router);
		}
	};

	const readOnly = taskState.status != 'todo' || isReadonly || isFrozen;

	const attemptsStr = task.attempts === 0 ? '' : `(${taskState.attempts + 1}/${task.attempts})`;

	useEffect(() => {
		if (task.type === 'fullscreen-video' && taskState.status == 'todo') {
			console.log('navigating to fullscreen video', task.id);
			navigate({ to: '/fullscreen-video/$roundId/$taskId', params: { roundId, taskId: task.id } });
		}
	}, [task, roundId, task.id, navigate, taskState.status]);
	return (
		<MobileScreen>
			<ScoreBadge style={{ marginLeft: 'auto' }}>
				{task.points} {__`points`}
			</ScoreBadge>
			{roundTitle && <Heading mode="tertiary">{roundTitle}</Heading>}
			<Heading mode="primary">{task.title}</Heading>
			{task.subtitle && <Heading mode="tertiary">{task.subtitle}</Heading>}

			{task.description && <RichTextContent content={task.description} />}

			<div className="mt-8">
				{task?.media && !(task.media.kind == 'audio' && task.type == 'augmented-reality') && (
					<BunnyMediaRenderer media={task.media} autoplay={task.autoplayMedia} />
				)}
			</div>

			{task.type == 'information' && (
				<div>
					<Button className="w-full" onClick={() => onSubmit({ kind: 'text', text: 'page-read' })} disabled={readOnly}>
						<CheckCheck /> {__`Read`}
					</Button>
				</div>
			)}

			{task.type == 'augmented-reality' && (
				<div>
					<ARFormComponent taskId={task.id} roundId={roundId} />
				</div>
			)}
			{task.type == 'bingo' && (
				<div>
					<BingoFormComponent
						taskId={task.id}
						roundId={roundId}
						readOnly={readOnly}
						task={task}
						answer={taskState.answer ? (taskState.answer as Extract<TaskAnswer, { kind: 'bingo' }>) : undefined}
					/>
				</div>
			)}
			{task.type == 'automatic-validation' && (
				<div>
					{task.typeAutomaticValidationConfig?.kind == 'open' && (
						<OpenQuestionForm answer={taskState.answer as TaskAnswerText | undefined} readOnly={readOnly} onSubmit={onSubmit} />
					)}
					{task.typeAutomaticValidationConfig?.kind == 'multiple-choice' && (
						<MultipleChoiceQuestionForm
							answer={taskState.answer as TaskAnswerSingleChoice | undefined}
							options={task.typeAutomaticValidationConfig?.multipleChoiceAnswers as MultipleChoiceAnswer[]}
							readOnly={readOnly}
							onSubmit={onSubmit}
						/>
					)}
					{task.typeAutomaticValidationConfig?.kind == 'multi-choice' && (
						<MultiSelectMultipleChoiceQuestionForm
							answer={taskState.answer as TaskAnswerMultipleChoice | undefined}
							options={task.typeAutomaticValidationConfig?.multipleChoiceAnswers as MultipleChoiceAnswer[]}
							readOnly={readOnly}
							onSubmit={onSubmit}
						/>
					)}
				</div>
			)}
			{task.type == 'validation-by-game-master' && (
				<div>
					{task.typeGameMasterValidationConfig!.kind === 'open' && (
						<OpenQuestionForm answer={taskState.answer as TaskAnswerText | undefined} readOnly={readOnly} onSubmit={onSubmit} />
					)}
					{task.typeGameMasterValidationConfig!.kind === 'photo' && (
						<PhotoQuestionForm
							answer={taskState.answer as TaskAnswerPhoto | undefined}
							readOnly={readOnly}
							onSubmit={onSubmit}
						/>
					)}
					{task.typeGameMasterValidationConfig!.kind === 'video' && (
						<VideoQuestionForm
							answer={taskState.answer as TaskAnswerVideo | undefined}
							readOnly={readOnly}
							onSubmit={onSubmit}
						/>
					)}
				</div>
			)}

			{taskState.status == 'todo' && taskState.answer != undefined && task.type != 'bingo' && (
				<Para className="mt-2">
					<Info className="inline-block" /> {__`Answer rejected, try again.`} {attemptsStr}
				</Para>
			)}
			{taskState.status == 'todo' && taskState.answer?.kind == 'bingo' && taskState.answer.bingo == true && task.type == 'bingo' && (
				<Para className="mt-2">
					<Info className="inline-block" /> {__`Bingo rejected, try again.`} {attemptsStr}
				</Para>
			)}
			{taskState.status == 'in-progress' &&
				taskState.isLocal &&
				(!isOnline ? (
					<Para className="mt-2">
						<TriangleAlert className="inline-block" /> {__`This task will be submitted when you are back online.`}
					</Para>
				) : (
					<Para className="mt-2">
						<Info className="inline-block" /> {__`Task submitted.`} {__`Waiting for validation.`}
					</Para>
				))}
			{taskState.status == 'in-progress' && !taskState.isLocal && (
				<Para className="mt-2">
					<Info className="inline-block" /> {__`Task submitted.`} {__`Please wait for this task to be reviewed by an admin.`}
				</Para>
			)}
			{taskState.status == 'done' && !game?.hideScore && (
				<>
					<Para className="mt-2">
						<Info className="inline-block" /> {__`Task submitted!`} {__`You received ${taskState.pointsGiven} ${__`points`}.`}
					</Para>

					{taskState.completionResponse && (
						<Para className="mt-2 border-l-4 border-[var(--skin-text)] p-2 italic">{taskState.completionResponse}</Para>
					)}
				</>
			)}
			{taskState.status == 'done' && game?.hideScore && (
				<>
					<Para className="mt-2">
						<Info className="inline-block" /> {__`Task submitted!`}
					</Para>
				</>
			)}
		</MobileScreen>
	);
};
